/**
 * Client for the accounting API.
 *
 * Shared because three adapters need it: the MCP server, the CLI, and later the
 * UI. Each one turns a request into a protocol of its own, but none of them
 * should be parsing the error envelope for itself.
 *
 * Nothing here is Node-specific — it uses `fetch` and works in a browser.
 */
/** An error the API reported, in its own vocabulary. */
/**
 * The prefix that marks a personal access token.
 *
 * Defined here, in the package both the API and every client depend on, so the
 * two cannot disagree about the spelling. The service layer re-exports it.
 */
export const ACCESS_TOKEN_PREFIX = 'accountyob_pat_';
export class ApiError extends Error {
    status;
    code;
    requestId;
    details;
    constructor(status, code, message, requestId, details) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.requestId = requestId;
        this.details = details;
    }
}
/** The API could not be reached at all. Distinct from a refusal. */
export class ApiUnreachableError extends Error {
    constructor(baseUrl, cause) {
        super(`Could not reach the API at ${baseUrl}.`, { cause });
        this.name = 'ApiUnreachableError';
    }
}
export class ApiClient {
    #baseUrl;
    #token;
    #cookie;
    #fetch;
    #credentials;
    #origin;
    constructor(options) {
        this.#baseUrl = options.baseUrl.replace(/\/$/, '');
        this.#token = options.token;
        this.#cookie = options.cookie;
        /*
          Bound to the global, not merely referenced.
    
          Stored on a field and called as `this.#fetch(...)`, the global fetch would
          be invoked with the ApiClient as its receiver. Browsers reject that:
    
            TypeError: Failed to execute 'fetch' on 'Window': Illegal invocation
    
          Node's undici does not check the receiver, so the CLI, the MCP server and
          every integration test pass either way — and the UI could not make a
          single request in a real browser. The defect was invisible to the whole
          test suite because nothing in it runs in one.
    
          An injected `fetch` is left alone: a caller passing a function has already
          decided what it should be bound to.
        */
        this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
        this.#credentials = options.credentials;
        this.#origin = options.origin;
    }
    /** Perform a request and return the parsed body, or throw. */
    async request(path, options = {}) {
        return (await this.requestFull(path, options)).body;
    }
    /**
     * The response body as text.
     *
     * For endpoints that return something other than JSON — the SAF-T export
     * returns XML, and parsing it as JSON would fail on the first character.
     * Errors still arrive as the usual JSON envelope, so those are handled the
     * same way.
     */
    async requestText(path, options = {}) {
        const response = await this.#send(path, options);
        return response.text();
    }
    /**
     * The response body as bytes, with the headers that describe them.
     *
     * For documents. An attachment is served by the API rather than fetched from
     * object storage with a signed URL, so the caller needs the same
     * authentication as any other request — which is exactly what this client
     * already attaches, and the reason this belongs here rather than in a bare
     * `fetch` at each call site.
     *
     * The filename comes back separately because it lives in `Content-Disposition`
     * and every caller would otherwise parse that header itself.
     */
    async requestBytes(path, options = {}) {
        const response = await this.#send(path, options);
        const bytes = new Uint8Array(await response.arrayBuffer());
        return {
            bytes,
            contentType: response.headers.get('content-type') ?? 'application/octet-stream',
            filename: filenameFromDisposition(response.headers.get('content-disposition')),
        };
    }
    /** As `request`, but keeps the status and headers — needed for Set-Cookie. */
    async requestFull(path, options = {}) {
        const response = await this.#send(path, options);
        const body = response.status === 204 ? undefined : (await response.json());
        return { status: response.status, body, headers: response.headers };
    }
    /** Everything up to reading the body, which the callers do differently. */
    async #send(path, options) {
        const headers = { 'content-type': 'application/json' };
        if (this.#token !== undefined) {
            // Resolved per request, not once at construction: a provider mints a
            // fresh token when the current one is close to expiring.
            const token = typeof this.#token === 'string' ? this.#token : await this.#token();
            headers['authorization'] = `Bearer ${token}`;
        }
        const cookie = options.cookie ?? this.#cookie;
        if (cookie !== undefined) {
            headers['cookie'] = cookie;
        }
        if (options.agentId !== undefined) {
            headers['x-agent-id'] = options.agentId;
        }
        if (options.idempotencyKey !== undefined) {
            headers['idempotency-key'] = options.idempotencyKey;
        }
        // Only ever set by a non-browser client; see the `origin` option.
        if (this.#origin !== undefined) {
            headers['origin'] = this.#origin;
        }
        const init = { method: options.method ?? 'GET', headers };
        if (this.#credentials !== undefined) {
            init.credentials = this.#credentials;
        }
        if (options.body !== undefined) {
            init.body = JSON.stringify(options.body);
        }
        let response;
        // Through a local, so the receiver is never this client. `this.#fetch(...)`
        // would pass the ApiClient as `this`, which is what a browser refuses on
        // the global fetch — and would be just as wrong for an injected one.
        const send = this.#fetch;
        try {
            response = await send(`${this.#baseUrl}${path}`, init);
        }
        catch (error) {
            // A refusal and an unreachable server are different problems and deserve
            // different advice, so they are different error types.
            throw new ApiUnreachableError(this.#baseUrl, error);
        }
        if (!response.ok) {
            const envelope = (await response.json().catch(() => null));
            throw new ApiError(response.status, envelope?.error ?? 'unknown_error', envelope?.message ?? `The API returned ${String(response.status)}.`, envelope?.request_id, envelope?.details);
        }
        return response;
    }
}
/**
 * The original filename out of a `Content-Disposition` header.
 *
 * `filename*=UTF-8''...` is preferred over `filename="..."` because it is the
 * one that survives a Danish name: the API writes both, and the ASCII form has
 * had every non-ASCII character replaced with an underscore. Reading the plain
 * one first would turn "Kvittering fra Føtex.pdf" into "Kvittering fra F_tex.pdf"
 * and write it to disk under that name.
 */
export function filenameFromDisposition(header) {
    if (header === null) {
        return undefined;
    }
    const extended = /filename\*=UTF-8''([^;]+)/iu.exec(header);
    if (extended?.[1] !== undefined) {
        try {
            return decodeURIComponent(extended[1].trim());
        }
        catch {
            // A malformed percent-encoding is not a reason to fail the download; the
            // plain form below is still usable.
        }
    }
    const plain = /filename="([^"]*)"/iu.exec(header);
    return plain?.[1];
}
//# sourceMappingURL=api-client.js.map